import { useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';
import SearchableSelect from '../searchable-select';
import useContactSearch from '../../hooks/use-contact-search';
import { SELECTION_METHOD_OPTIONS } from '../../utils/cadence-constants';

/**
 * Audience section for the cadence setup step.
 *
 * Layout:
 *   1. ADD RECIPIENTS — method pill buttons + conditional picker (list / saved search / contacts).
 *      Selections are only enrolled when the cadence is saved.
 *
 *   2. Connected audience groups (bottom, collapsible, default collapsed) — one per type.
 *      Each row shows the source label and a Disconnect button that fires an immediate mutation.
 *
 * Safety: disconnect buttons are hidden while audiencesLoaded is false so the
 * user cannot accidentally remove audiences before the backend data arrives.
 */
const AudienceSection = ({
  isProspectPlan,
  existingAudiences,
  audiencesLoaded,
  contactLists,
  savedSearches,
  onDisconnect,
  onDisconnectAllManual,
  selectionMethod,
  setSelectionMethod,
  selectedList,
  setSelectedList,
  selectedSavedSearch,
  setSelectedSavedSearch,
  selectedContacts,
  setSelectedContacts,
  readonly,
  canDelete,
}) => {
  const [disconnecting, setDisconnecting] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const { query, setQuery, results, loading } = useContactSearch();

  const listAudiences = (existingAudiences || []).filter(
    (a) => a.audienceType === 'list' || (a.audienceType == null && a.contactListId)
  );
  const searchAudiences = (existingAudiences || []).filter(
    (a) => a.audienceType === 'saved_search' || (a.audienceType == null && a.savedSearchTagId)
  );
  const individualAudiences = (existingAudiences || []).filter(
    (a) => a.audienceType === 'individual' || (a.audienceType == null && a.contactId != null)
  );

  const hasConnected = listAudiences.length > 0 || searchAudiences.length > 0 || individualAudiences.length > 0;

  const getListLabel = (id) => contactLists?.find((l) => String(l.value) === String(id))?.label || `List #${id}`;
  const getSearchLabel = (id) => savedSearches?.find((s) => String(s.value) === String(id))?.label || `Saved Search`;

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleDisconnect = async (audienceId) => {
    setDisconnecting(audienceId);
    try {
      await onDisconnect(audienceId);
    } finally {
      setDisconnecting(null);
    }
  };

  const handleDisconnectAllManual = async () => {
    setDisconnecting('all-manual');
    try {
      await onDisconnectAllManual(individualAudiences);
    } finally {
      setDisconnecting(null);
    }
  };

  const addContact = (contact) => {
    const attrs = contact.properties || {};
    const name =
      contact.name ||
      attrs.name_full?.value ||
      [attrs.name_first?.value, attrs.name_last?.value].filter(Boolean).join(' ') ||
      'Unknown';
    const firstEmail = contact.emails?.[0];
    const email = firstEmail?.email?.value || firstEmail?.email || '';
    if (isProspectPlan) {
      setSelectedContacts([{ id: contact.id, name, email }]);
    } else {
      setSelectedContacts((prev) => {
        if (prev.some((c) => c.id === contact.id)) return prev;
        return [...prev, { id: contact.id, name, email }];
      });
    }
    setQuery('');
  };

  const removeContact = (id) => setSelectedContacts((prev) => prev.filter((c) => c.id !== id));

  const pendingCount = (selectedList ? 1 : 0) + (selectedSavedSearch ? 1 : 0) + selectedContacts.length;

  // ── Inline contact search (shared by contacts method + prospect plan) ──────
  const renderContactSearch = () => (
    <div className="create-cadence-configure--field">
      <label className="create-cadence-configure--field-label">
        {isProspectPlan ? 'SEARCH CONTACT' : 'SEARCH CONTACTS'}
      </label>
      {isProspectPlan && selectedContacts.length === 0 && (
        <p className="create-cadence-configure--csv-hint">
          A prospect plan is designed for a single contact. Select the person you want to create a personalized outreach
          plan for.
        </p>
      )}
      {(!isProspectPlan || selectedContacts.length === 0) && (
        <>
          <div className="create-cadence-configure--contact-search">
            <Icon icon="search" />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={readonly}
            />
            {loading && <Icon icon="refresh" className="create-cadence-configure--spinner" />}
          </div>
          {results.length > 0 && (
            <div className="create-cadence-configure--contact-results">
              {results.map((contact) => {
                const attrs = contact.properties || {};
                const name =
                  contact.name ||
                  attrs.name_full?.value ||
                  [attrs.name_first?.value, attrs.name_last?.value].filter(Boolean).join(' ') ||
                  'Unknown';
                const firstEmail = contact.emails?.[0];
                const email = firstEmail?.email?.value || firstEmail?.email || '';
                const isSelected = selectedContacts.some((c) => c.id === contact.id);
                return (
                  <button
                    key={contact.id}
                    type="button"
                    className={`create-cadence-configure--contact-result${isSelected ? ' is-selected' : ''}`}
                    onClick={() => addContact(contact)}
                  >
                    <div className="create-cadence-configure--contact-info">
                      <span className="create-cadence-configure--contact-name">{name}</span>
                      {email && <span className="create-cadence-configure--contact-email">{email}</span>}
                    </div>
                    <Icon icon={isSelected ? 'check-circle' : 'add-circle-outline'} />
                  </button>
                );
              })}
            </div>
          )}
          {query.length > 0 && query.length < 2 && (
            <p className="create-cadence-configure--hint">Type at least 2 characters to search.</p>
          )}
        </>
      )}
      {selectedContacts.length > 0 && (
        <div className="create-cadence-configure--selected-contacts">
          <label className="create-cadence-configure--field-label">
            {isProspectPlan ? 'SELECTED CONTACT' : `TO ENROLL ON SAVE (${selectedContacts.length})`}
          </label>
          <div className="create-cadence-configure--contact-chips">
            {selectedContacts.map((contact) => (
              <span key={contact.id} className="create-cadence-configure--contact-chip">
                {contact.name}
                {!readonly && (
                  <button type="button" onClick={() => removeContact(contact.id)}>
                    <Icon icon="close" />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ── Collapsible connected-audience group ────────────────────────────────
  const renderConnectedGroup = ({ key, label, count, rows }) => {
    const isOpen = !!openGroups[key];
    return (
      <div key={key} className="audience-section--connected-group">
        <button
          type="button"
          className="audience-section--connected-group-header"
          onClick={() => toggleGroup(key)}
          aria-expanded={isOpen}
        >
          <span className="audience-section--connected-group-label">{label}</span>
          <span className="audience-section--count-badge">{count}</span>
          <Icon icon={isOpen ? 'chevron-up' : 'chevron-down'} className="audience-section--connected-group-chevron" />
        </button>
        {isOpen && <div className="audience-section--list">{rows}</div>}
      </div>
    );
  };

  return (
    <div className="audience-section">
      {/* ── Add recipients ────────────────────────────────────────────────── */}
      <div className="audience-section--add">
        <label className="create-cadence-configure--field-label">ADD RECIPIENTS</label>

        {!canDelete ? (
          <p className="create-cadence-configure--permission-notice">
            Only the cadence owner can add or remove recipients.
          </p>
        ) : (
          <>
            {isProspectPlan ? (
              renderContactSearch()
            ) : (
              <>
                <div className="audience-section--method-pills">
                  {SELECTION_METHOD_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`audience-section--method-pill${selectionMethod?.value === opt.value ? ' is-active' : ''}`}
                      onClick={() => setSelectionMethod(opt)}
                      disabled={readonly}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {selectionMethod?.value === 'list' && (
                  <div className="create-cadence-configure--field">
                    <label className="create-cadence-configure--field-label">CHOOSE LIST</label>
                    <SearchableSelect
                      options={contactLists}
                      value={selectedList}
                      onChange={setSelectedList}
                      placeholder="Select a list…"
                      disabled={readonly}
                    />
                    {selectedList && (
                      <div className="create-cadence-configure--selection-info">
                        <Icon icon="check-circle" />
                        <span>
                          {selectedList.count != null && selectedList.count > 0
                            ? `${selectedList.count.toLocaleString()} recipients in this list`
                            : 'Loading recipient count…'}
                        </span>
                        {!readonly && (
                          <button
                            type="button"
                            className="create-cadence-configure--clear-selection"
                            onClick={() => setSelectedList(null)}
                          >
                            <Icon icon="close" /> Clear
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {selectionMethod?.value === 'search' && (
                  <div className="create-cadence-configure--field">
                    <label className="create-cadence-configure--field-label">CHOOSE SAVED SEARCH</label>
                    <SearchableSelect
                      options={savedSearches}
                      value={selectedSavedSearch}
                      onChange={setSelectedSavedSearch}
                      placeholder="Select a saved search…"
                      disabled={readonly}
                    />
                    {selectedSavedSearch && (
                      <div className="create-cadence-configure--selection-info">
                        <Icon icon="check-circle" />
                        <span>Recipients matching &ldquo;{selectedSavedSearch.label}&rdquo; will be enrolled</span>
                        {!readonly && (
                          <button
                            type="button"
                            className="create-cadence-configure--clear-selection"
                            onClick={() => setSelectedSavedSearch(null)}
                          >
                            <Icon icon="close" /> Clear
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {selectionMethod?.value === 'contacts' && renderContactSearch()}
              </>
            )}

            {pendingCount > 0 && (
              <p className="audience-section--pending-note">
                <Icon icon="info" /> {pendingCount === 1 ? '1 new audience' : `${pendingCount} new items`} will be added
                when you save.
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Connected audiences (collapsible groups, bottom) ─────────────── */}
      {audiencesLoaded && hasConnected && (
        <div className="audience-section--connected-groups">
          {listAudiences.length > 0 &&
            renderConnectedGroup({
              key: 'lists',
              label: 'CURRENTLY CONNECTED LISTS',
              count: listAudiences.length,
              rows: listAudiences.map((a) => (
                <div key={a.id} className="audience-section--row">
                  <Icon icon="list-view" className="audience-section--row-icon" />
                  <div className="audience-section--row-info">
                    <span className="audience-section--row-label">{getListLabel(a.contactListId)}</span>
                    <span className="audience-section--row-type">LIST</span>
                  </div>
                  {canDelete && !readonly && (
                    <button
                      type="button"
                      className="audience-section--disconnect-btn"
                      onClick={() => handleDisconnect(a.id)}
                      disabled={disconnecting === a.id}
                    >
                      {disconnecting === a.id ? 'Removing…' : 'Disconnect'}
                    </button>
                  )}
                </div>
              )),
            })}

          {searchAudiences.length > 0 &&
            renderConnectedGroup({
              key: 'searches',
              label: 'CURRENTLY CONNECTED SAVED SEARCHES',
              count: searchAudiences.length,
              rows: searchAudiences.map((a) => (
                <div key={a.id} className="audience-section--row">
                  <Icon icon="search" className="audience-section--row-icon" />
                  <div className="audience-section--row-info">
                    <span className="audience-section--row-label">{getSearchLabel(a.savedSearchTagId)}</span>
                    <span className="audience-section--row-type">SAVED SEARCH</span>
                  </div>
                  {canDelete && !readonly && (
                    <button
                      type="button"
                      className="audience-section--disconnect-btn"
                      onClick={() => handleDisconnect(a.id)}
                      disabled={disconnecting === a.id}
                    >
                      {disconnecting === a.id ? 'Removing…' : 'Disconnect'}
                    </button>
                  )}
                </div>
              )),
            })}

          {individualAudiences.length > 0 &&
            renderConnectedGroup({
              key: 'contacts',
              label: 'CURRENTLY CONNECTED CONTACTS',
              count: individualAudiences.length,
              rows: (
                <div className="audience-section--row">
                  <Icon icon="group" className="audience-section--row-icon" />
                  <div className="audience-section--row-info">
                    <span className="audience-section--row-label">
                      {individualAudiences.length.toLocaleString()} manually added contact
                      {individualAudiences.length !== 1 ? 's' : ''}
                    </span>
                    <span className="audience-section--row-type">CONTACTS</span>
                  </div>
                  {canDelete && !readonly && (
                    <button
                      type="button"
                      className="audience-section--disconnect-btn audience-section--disconnect-btn-danger"
                      onClick={handleDisconnectAllManual}
                      disabled={disconnecting === 'all-manual'}
                      // TODO(backend): replace the client-side loop in onDisconnectAllManual with a
                      // bulk delete mutation. Currently calls deleteCadenceAudience N times.
                    >
                      {disconnecting === 'all-manual' ? 'Removing…' : 'Remove all'}
                    </button>
                  )}
                </div>
              ),
            })}
        </div>
      )}
    </div>
  );
};

AudienceSection.propTypes = {
  isProspectPlan: PropTypes.bool,
  existingAudiences: PropTypes.array,
  audiencesLoaded: PropTypes.bool,
  contactLists: PropTypes.array,
  savedSearches: PropTypes.array,
  onDisconnect: PropTypes.func.isRequired,
  onDisconnectAllManual: PropTypes.func.isRequired,
  selectionMethod: PropTypes.object,
  setSelectionMethod: PropTypes.func.isRequired,
  selectedList: PropTypes.object,
  setSelectedList: PropTypes.func.isRequired,
  selectedSavedSearch: PropTypes.object,
  setSelectedSavedSearch: PropTypes.func.isRequired,
  selectedContacts: PropTypes.array,
  setSelectedContacts: PropTypes.func.isRequired,
  readonly: PropTypes.bool,
  canDelete: PropTypes.bool,
};

AudienceSection.defaultProps = {
  isProspectPlan: false,
  existingAudiences: [],
  audiencesLoaded: false,
  contactLists: [],
  savedSearches: [],
  selectedContacts: [],
  readonly: false,
  canDelete: true,
};

export default AudienceSection;
